import React, { Component } from 'react';
import PropTypes from 'prop-types';
import {
  Modal,
  ModalHeader,
  ModalContent,
  ModalFooter,
  Toaster,
} from '@knockrentals/knock-react';
import SourcesAPI from '../SourcesAPI';
import {
  GREETING_URL_PAYLOAD_KEY,
  RECORDING_TYPES,
  GREETING_MODAL_TITLES,
} from './constants';
import './_greetingRecordingEditor.scss';

class GreetingRecordingEditor extends Component {
  state = {
    recordingType: RECORDING_TYPES.PROSPECT,
    originalRecordingUrl: '',
    uploadedRecordingUrl: '',
    testPhoneNumber: '',
    isUploadingFile: false,
    isAudioLoading: false,
    isAudioPlaying: false,
  };

  _audioPlayer = null;
  _fileInput = null;

  componentWillMount() {
    this.setState({
      recordingType: this.props.recordingType,
      isAudioLoading: true,
    });

    SourcesAPI.getPhoneRecordingTemplates(this.props.propertyId).then(
      ({ phone_recording_templates }) => {
        const recordingTemplate =
          GREETING_URL_PAYLOAD_KEY[this.state.recordingType];

        const originalRecordingUrl =
          phone_recording_templates[recordingTemplate];

        this.setState({ originalRecordingUrl, isAudioLoading: false });
      }
    );
  }

  render() {
    const recordingUrl = this.state.uploadedRecordingUrl
      ? this.state.uploadedRecordingUrl
      : this.state.originalRecordingUrl;

    return (
      <Modal
        className="greeting-recording-editor"
        onBackdropClicked={this.props.onCancel}
      >
        <form onSubmit={this.onSubmit.bind(this)} autoComplete="off">
          <ModalHeader>
            <h3>{GREETING_MODAL_TITLES[this.state.recordingType]}</h3>
          </ModalHeader>

          <ModalContent>
            <div className="hidden-elements">
              <audio
                ref={(player) => {
                  this._audioPlayer = player;
                }}
                onEnded={this.toggleAudioPlayingState.bind(this, false)}
                controls
              >
                <source src={recordingUrl} type="audio/mpeg" />
                Your browser does not support the audio element.
              </audio>
              <input
                ref={(input) => {
                  this._fileInput = input;
                }}
                className="recording-file-input"
                type="file"
                accept="audio/wav,audio/mpeg"
                onChange={this.handleFileFromInputChange.bind(this)}
              />
            </div>

            <div className="knock-react-flex">
              <div className="title-column">
                <strong>Recording:</strong>
              </div>
              <div>
                <div>
                  <em>{this.extractNameFromRecordingUrl(recordingUrl)}</em>
                </div>
                <div>
                  {this.renderPlayerActions()}
                  {this.renderRecordingActions()}
                </div>
              </div>
            </div>

            <hr />

            <div className="knock-react-flex">
              <div className="title-column">
                <strong>Test via:</strong>
              </div>
              <div className="value-column">
                <div>
                  <input
                    className="knock-react-input input-longer"
                    value={this.state.testPhoneNumber}
                    name="testPhoneNumber"
                    placeholder="Phone number to dial"
                    onChange={this.inputChanged.bind(
                      this,
                      'testPhoneNumber',
                      'value'
                    )}
                  />
                </div>
                <div>
                  <button
                    className="knock-react-button btn-success mTop5 margin-none"
                    onClick={this.placeRecordingTestCall.bind(this)}
                    disabled={this.state.isAudioLoading}
                  >
                    <i className="fa fa-phone-square" /> Test recording in call
                  </button>
                </div>
              </div>
            </div>
          </ModalContent>

          <ModalFooter>
            <button
              className="knock-react-button"
              onClick={this.props.onCancel}
            >
              Cancel
            </button>
            <button
              className="knock-react-button"
              disabled={!this.state.uploadedRecordingUrl}
              onClick={this.onSave.bind(this)}
            >
              Save
            </button>
          </ModalFooter>
        </form>
      </Modal>
    );
  }

  renderPlayerActions() {
    if (this.state.isAudioLoading) {
      return (
        <button
          className="knock-react-button corner-right btn-sm margin-none"
          disabled
        >
          <i className="fa fa-spin fa-spinner" /> Loading
        </button>
      );
    } else {
      if (this.state.isAudioPlaying) {
        return (
          <button
            className="knock-react-button corner-right btn-sm margin-none"
            onClick={this.stopAudio.bind(this)}
          >
            <i className="fa fa-stop-circle" /> Stop
          </button>
        );
      } else {
        return (
          <button
            className="knock-react-button corner-right btn-sm margin-none"
            onClick={this.playAudio.bind(this)}
          >
            <i className="fa fa-play-circle" /> Listen
          </button>
        );
      }
    }
  }

  renderRecordingActions() {
    if (this.state.uploadedRecordingUrl) {
      return (
        <button
          className="knock-react-button btn-warning btn-sm corner-left margin-none"
          onClick={this.clearUploadedRecordingUrl.bind(this)}
        >
          <i className="fa fa-undo" /> Revert to original
        </button>
      );
    } else {
      if (this.state.isUploadingFile) {
        return (
          <span className="knock-react-button btn-success mLeft10 text-small corner-left margin-none">
            <i className="fa fa-spinner fa-spin" /> Uploading Audio
          </span>
        );
      } else {
        return (
          <button
            className="knock-react-button btn-success btn-sm corner-left margin-none"
            onClick={this.openFileInputDialog.bind(this)}
          >
            <i className="fa fa-upload" /> Upload New Recording
          </button>
        );
      }
    }
  }

  extractNameFromRecordingUrl(recordingUrl) {
    if (!recordingUrl) {
      return;
    }
    return recordingUrl?.split('/').pop();
  }

  openFileInputDialog() {
    if (this._fileInput) {
      this.stopAudio();
      this._fileInput.click();
    }
  }

  playAudio() {
    this._audioPlayer.load();
    this._audioPlayer.play();
    this.toggleAudioPlayingState(true);
  }

  stopAudio() {
    this._audioPlayer.pause();
    this.toggleAudioPlayingState(false);
  }

  toggleAudioPlayingState(isAudioPlaying) {
    this.setState({ isAudioPlaying });
  }

  clearUploadedRecordingUrl() {
    this.stopAudio();
    this.setState({ uploadedRecordingUrl: '' });
  }

  handleFileFromInputChange(event) {
    const file = event.target.files[0];

    this.setState({
      isAudioLoading: true,
      isUploadingFile: true,
    });

    SourcesAPI.uploadPhoneRecordingFile(file).then(({ recording_url }) => {
      this.setState({
        isAudioLoading: false,
        isUploadingFile: false,
        uploadedRecordingUrl: recording_url,
      });
    });
  }

  placeRecordingTestCall() {
    const phoneNumber = this.state.testPhoneNumber;

    if (!phoneNumber) {
      Toaster.showToast(
        `Test phone number is invalid`,
        2000,
        Toaster.ToastClasses.error
      );
      return;
    }

    const recordingUrl = this.state.uploadedRecordingUrl
      ? this.state.uploadedRecordingUrl
      : this.state.originalRecordingUrl;

    SourcesAPI.placeTestCallForPhoneRecordingUrl(phoneNumber, recordingUrl)
      .then(() => {
        Toaster.showToast(
          `Test call made to ${phoneNumber}`,
          2000,
          Toaster.ToastClasses.success
        );
      })
      .catch(() => {
        Toaster.showToast(
          `Failed to place call for ${recordingUrl} to ${phoneNumber}`,
          2000,
          Toaster.ToastClasses.error
        );
      });
  }

  inputChanged(key, target, event) {
    this.setState({
      [key]: event.target[target],
    });
  }

  async onSave() {
    if (this.state.uploadedRecordingUrl) {
      if (this.state.recordingType === RECORDING_TYPES.APPLICANT) {
        try {
          await SourcesAPI.updatePhoneRecordingTemplates(
            this.props.propertyId,
            this.state.uploadedRecordingUrl,
            this.state.recordingType
          );
          Toaster.showToast(
            `Saved ${GREETING_MODAL_TITLES[this.state.recordingType]}`,
            2000,
            Toaster.ToastClasses.success
          );
          this.props.onCancel();

          return;
        } catch {
          Toaster.showToast(
            'Failed to update applicant greeting audio',
            2000,
            Toaster.ToastClasses.error
          );
          return;
        }
      }

      if (this.state.recordingType === RECORDING_TYPES.PROSPECT) {
        SourcesAPI.putPhoneRecordingTemplates(
          this.props.propertyId,
          this.state.uploadedRecordingUrl
        ).then(() => {
          this.props.onCancel();
        });
      } else {
        SourcesAPI.putPhoneResidentRecordingTemplates(
          this.props.propertyId,
          this.state.uploadedRecordingUrl
        ).then(() => {
          this.props.onCancel();
        });
      }
    }
  }

  onSubmit(event) {
    event.preventDefault();
  }
}

export default GreetingRecordingEditor;

GreetingRecordingEditor.propTypes = {
  propertyId: PropTypes.number,
  recordingType: PropTypes.oneOf([
    RECORDING_TYPES.APPLICANT,
    RECORDING_TYPES.PROSPECT,
    RECORDING_TYPES.RESIDENT,
  ]),
  onCancel: PropTypes.func,
};
